import React, { FC, Fragment, useState } from "react";
import type { SupportCard } from "../../services/ApiService";
import OrderCard from "./OrderCard";
import {
  AnswerBlock,
  AnswerFigure,
  ImageOverlay,
  OrderedSteps,
  UnorderedSteps,
} from "./SupportAssistant.styles";

/**
 * One screenshot from the printed CSM guides, as the BFF sends it.
 *
 * `data` is a `data:` URI, not a URL — see the validation below for why that is the whole
 * security story for this component.
 */
export interface SupportImage {
  key: string;
  caption: string;
  data: string;
}

/**
 * Renders an assistant answer.
 *
 * WHY THIS EXISTS RATHER THAN react-markdown
 * ------------------------------------------
 * The prompt has always asked the model to put on-screen words in **bold** and to write
 * numbered steps, and the panel rendered `turn.content` as a plain text node — so every
 * operator has been reading literal asterisks around every button name. This fixes that, and
 * it is also what lets an answer show the guide's screenshot of the screen it is describing.
 *
 * A full markdown renderer would do more than is wanted here. The grammar below is CLOSED: bold,
 * numbered and bulleted runs, paragraphs, `[[image:key]]` and `[[card:n]]` — no links, no raw
 * HTML, no `dangerouslySetInnerHTML`. Anything it does not recognise stays text, escaped by React
 * as normal. That matters because the text being rendered is model output, and job notes, ERP
 * comments and source text all reach that model.
 *
 * ⚠️ AND THE CARD IS THE ONE PLACE `renderInline` MUST NOT REACH. Its fields are ERP and job
 * data, not prose — a customer called `**Acme**` has to render as asterisks. See `OrderCard`.
 *
 * ⚠️ THE IMAGE RULE IS THE LOAD-BEARING ONE. `img-src` in the SWA's CSP permits `https:`, so an
 * `<img>` whose source came from the response could be pointed at a third party and used to
 * exfiltrate whatever the assistant just read — the classic injected-image leak. `isRenderable`
 * accepts ONLY a base64 `data:` URI of a PNG or JPEG, which cannot express a destination at
 * all, so an `<img>` in this panel can never make a network request. Do not relax it to allow a
 * URL "just for hosting"; the moment images are served rather than embedded, that guarantee is
 * gone and needs replacing with something else.
 */

/**
 * `[[image:some-key]]` alone on a line.
 *
 * ⚠️ THIS IS A CONTRACT WITH THE BFF, AND IT HAS BEEN BROKEN ONCE. A picture is positioned by
 * its marker: `images` is only ever consulted for a line that matches this, so an answer whose
 * markers were removed upstream renders NO screenshots at all, however complete its `images`
 * array is — and the failure is silent on both sides, because a marker with no image and an
 * image with no marker are each meant to render nothing. `HelpImageGuard.Resolve` in the
 * Function App is the other half: it keeps exactly the marker of each picture it sends, alone
 * on its line. Neither side may be changed without the other.
 */
const IMAGE_LINE = /^\s*\[\[image:\s*([a-z0-9-]+)\s*\]\]\s*$/i;

/**
 * `[[card:1]]` alone on a line — one production order, drawn where the answer put it.
 *
 * ⚠️ THE DIGIT IS A SLOT, NOT AN ORDER NUMBER, AND THAT IS THE WHOLE DEFENCE. It is the card's
 * 1-based position in `cards`, so this looks up `cards[n - 1]`.
 *
 * The Function App writes the marker this way ON PURPOSE. The model emits
 * `[[card:order:4501234]]`; `Support/Tools/CardGuard.cs` resolves it against what that request's
 * tools actually fetched and rewrites it to a digit. It has to, because `OrderNumberShape` over
 * there permits `.`, `_`, `/` and `-` — so a real order like `PO/123` left in the marker would
 * match neither this regex nor `MARKER_LINE`, and would reach the operator as LITERAL TEXT in
 * the middle of an otherwise correct answer. That is exactly how the `[[image:]]` contract broke,
 * and it was silent for three months. Two charsets on two sides collapse to one digit.
 *
 * ⚠️ NEITHER SIDE MAY CHANGE WITHOUT THE OTHER. `CardGuardTests.TheClientContract` restates this
 * literal; it is the only place the two are compared.
 */
const CARD_LINE = /^\s*\[\[card:(\d{1,2})\]\]\s*$/;

/**
 * ANY `[[family:…]]` marker alone on a line — recognised or not.
 *
 * ⚠️ THIS IS THE CATCH-ALL, AND IT IS CHEAP INSURANCE ON A CONTRACT THAT HAS ALREADY BROKEN
 * ONCE SILENTLY. `[[image:…]]` used to be the only marker family; there are now several, and
 * the two repos can disagree about them in either direction — a newer Function App emitting a
 * family this build predates, a marker whose key this build cannot resolve, or a family that has
 * been RETIRED while an older Function App is still deployed. Without this, every one of those
 * falls through to the paragraph branch and the operator reads `[[walkthrough:create-user]]` as
 * literal text in the middle of an answer.
 *
 * ⚠️ That third case is no longer hypothetical: `[[walkthrough:…]]` was retired when the
 * spotlight tours moved to the per-screen "?", and the Function App’s `AnswerActionGuard`
 * deliberately stopped matching it rather than keeping a list of dead families to strip. This
 * line is what makes that safe, in both repositories, for every family removed after it.
 *
 * An unrecognised marker renders NOTHING, which is the same rule an unresolvable image already
 * follows: the marker is an aid, and its absence must cost the reader nothing.
 *
 * Note this does NOT widen the grammar. Markers still name keys, never destinations — the
 * chips that act on them are rendered OUTSIDE the bubble by `SupportAssistant`, resolved
 * through `utility/routes.ts` and `utility/supportActions.ts`. This renderer remains unable to
 * express a URL at all, which with `img-src https:` in the CSP is the whole point.
 */
const MARKER_LINE = /^\s*\[\[\s*[a-z0-9-]+\s*:[^[\]]*\]\]\s*$/i;

/**
 * The answer with EVERY marker line — image, link, action, follow-up, any family added later
 * and any family since retired — removed.
 *
 * For the paths that reproduce an answer as PLAIN TEXT: today the escalation e-mail's
 * transcript. The BFF leaves the markers in so this component can place what they position;
 * anywhere they will not become anything they are noise to whoever is reading. Mirrors
 * `HelpImageGuard.Strip` on the server, which does the same job for the same reason.
 *
 * It strips by FAMILY SHAPE rather than by an enumerated list, so a family this build has never
 * heard of still cannot reach a support engineer's inbox as literal text. That is the same
 * reasoning as `MARKER_LINE` above, applied to the other output path — the two used to be one
 * regex, and the one that got forgotten was always going to be this one.
 */
export const stripAnswerMarkers = (content: string): string =>
  content
    .split(/\r?\n/)
    .filter((line) => !MARKER_LINE.test(line))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

/** A base64 PNG or JPEG data URI, and nothing else. See the note above. */
const RENDERABLE_DATA = /^data:image\/(png|jpeg);base64,[A-Za-z0-9+/]+=*$/;

const isRenderable = (image?: SupportImage): image is SupportImage =>
  Boolean(image && typeof image.data === "string" && RENDERABLE_DATA.test(image.data));

/** `**bold**` → <strong>. The only inline rule; everything else is literal. */
const renderInline = (text: string): React.ReactNode =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, index) =>
    part.startsWith("**") && part.endsWith("**") && part.length > 4 ? (
      <strong key={index}>{part.slice(2, -2)}</strong>
    ) : (
      <Fragment key={index}>{part}</Fragment>
    )
  );

type Block =
  | { kind: "p"; lines: string[] }
  | { kind: "ol"; start: number; items: string[] }
  | { kind: "ul"; items: string[] }
  | { kind: "img"; imageKey: string }
  | { kind: "card"; slot: number };

const ORDERED = /^\s*(\d+)[.)]\s+(.*)$/;
const BULLETED = /^\s*[-*]\s+(.*)$/;

/**
 * Groups the answer into blocks.
 *
 * Consecutive numbered or bulleted lines become ONE list — the model writes steps as separate
 * lines, and rendering each as its own list restarts the numbering at 1 every time, which is
 * worse than no list at all.
 *
 * ⚠️ An `ol` carries the number the model actually WROTE, and the renderer passes it to the
 * `<ol start>`. A screenshot sits between the steps it illustrates, and an image block ends the
 * list it interrupts — so a corpus answer with pictures at steps 3, 5 and 6 becomes four
 * separate lists, and without this every one of them after the first would begin again at 1.
 * That misnumbering is invisible until the pictures work, which is exactly when it appears.
 */
function toBlocks(content: string): Block[] {
  const blocks: Block[] = [];
  let current: Block | null = null;

  const flush = () => {
    if (current) blocks.push(current);
    current = null;
  };

  for (const line of content.split(/\r?\n/)) {
    const image = line.match(IMAGE_LINE);
    if (image) {
      flush();
      blocks.push({ kind: "img", imageKey: image[1].toLowerCase() });
      continue;
    }

    // Before MARKER_LINE, which would otherwise swallow it as an unrecognised family — the
    // catch-all matches any `[[family:…]]` line and a card is one. Like an image, it ends the
    // block it interrupts, so the `<ol start>` rule below keeps the step numbers running.
    const card = line.match(CARD_LINE);
    if (card) {
      flush();
      blocks.push({ kind: "card", slot: Number(card[1]) });
      continue;
    }

    // Any OTHER marker family, and any image marker whose key this build cannot parse. It
    // ends the block it interrupts (exactly as an image does — the chips it stands for are
    // rendered after the bubble) and contributes nothing itself. See MARKER_LINE.
    if (MARKER_LINE.test(line)) {
      flush();
      continue;
    }

    if (!line.trim()) {
      flush();
      continue;
    }

    const ordered = line.match(ORDERED);
    if (ordered) {
      if (current?.kind !== "ol") flush();
      if (current?.kind !== "ol") {
        current = { kind: "ol", start: Number(ordered[1]) || 1, items: [] };
      }
      (current as { kind: "ol"; start: number; items: string[] }).items.push(ordered[2]);
      continue;
    }

    const bulleted = line.match(BULLETED);
    if (bulleted) {
      if (current?.kind !== "ul") flush();
      if (current?.kind !== "ul") current = { kind: "ul", items: [] };
      (current as { kind: "ul"; items: string[] }).items.push(bulleted[1]);
      continue;
    }

    // A continuation line inside a list belongs to the step above it, not to a new paragraph.
    if (current?.kind === "ol" || current?.kind === "ul") {
      const items = (current as { items: string[] }).items;
      items[items.length - 1] += " " + line.trim();
      continue;
    }

    if (current?.kind !== "p") flush();
    if (current?.kind !== "p") current = { kind: "p", lines: [] };
    (current as { kind: "p"; lines: string[] }).lines.push(line.trim());
  }

  flush();
  return blocks;
}

interface Props {
  content: string;
  images?: SupportImage[];

  /**
   * The order cards this answer carries, in slot order. A `[[card:n]]` line draws `cards[n - 1]`
   * and nothing else is consulted — same contract as `images`, and for the same reason.
   */
  cards?: SupportCard[];
}

const AnswerText: FC<Props> = ({ content, images, cards }) => {
  /**
   * The screenshot currently shown full size.
   *
   * The panel is ~380px wide and these are full-page screenshots of a dense manager table —
   * legible enough to recognise the screen, not to read. Clicking enlarges.
   */
  const [enlarged, setEnlarged] = useState<SupportImage | null>(null);

  const byKey = new Map((images ?? []).map((image) => [image.key.toLowerCase(), image]));

  return (
    <>
      {toBlocks(content).map((block, index) => {
        if (block.kind === "img") {
          const image = byKey.get(block.imageKey);

          // A marker with no image, or one that failed validation, renders NOTHING. The
          // picture is an aid; its absence must cost the reader nothing.
          if (!isRenderable(image)) return null;

          return (
            <AnswerFigure
              key={index}
              onClick={() => setEnlarged(image)}
              title="Click to enlarge"
            >
              <img
                src={image.data}
                alt={image.caption}
                loading="lazy"
                // Belt and braces: if the base64 is truncated or the MIME is wrong, drop the
                // whole figure rather than leaving a broken-image icon in the answer.
                onError={(e) => {
                  const figure = (e.target as HTMLElement).closest("figure");
                  if (figure) figure.style.display = "none";
                }}
              />
              <figcaption>{image.caption}</figcaption>
            </AnswerFigure>
          );
        }

        if (block.kind === "card") {
          const card = (cards ?? [])[block.slot - 1];

          // A marker with no card behind it renders NOTHING — the same rule an unresolvable
          // image already follows. It is what an older Function App, a switched-off
          // `SupportBot__DataCardsEnabled`, or a non-manager all look like from here, and none
          // of those is a fault the reader should be shown.
          if (!card) return null;

          return <OrderCard key={index} card={card} />;
        }

        if (block.kind === "ol") {
          return (
            <OrderedSteps key={index} start={block.start}>
              {block.items.map((item, i) => (
                <li key={i}>{renderInline(item)}</li>
              ))}
            </OrderedSteps>
          );
        }

        if (block.kind === "ul") {
          return (
            <UnorderedSteps key={index}>
              {block.items.map((item, i) => (
                <li key={i}>{renderInline(item)}</li>
              ))}
            </UnorderedSteps>
          );
        }

        return <AnswerBlock key={index}>{renderInline(block.lines.join(" "))}</AnswerBlock>;
      })}

      {enlarged && (
        <ImageOverlay
          role="dialog"
          aria-label={enlarged.caption}
          onClick={() => setEnlarged(null)}
        >
          <img src={enlarged.data} alt={enlarged.caption} />
          <span>{enlarged.caption} — click anywhere to close</span>
        </ImageOverlay>
      )}
    </>
  );
};

export default AnswerText;
